import { useEffect, useRef, useState } from "react";
import UserService from "src/Services/API/user.service";
import TimezoneDropdown from "./timezonedropdown";
import TimecardCycleDropdown from "./TimecardCycleDropdown";
import { getBranchLogoUrl } from "src/Common/common";
import { formatUSPhone } from "src/Common/function";

function AddModal({ branch, onAddClicked, onUpateClicked, onCancelClicked }) {
  const currentUser = UserService.getCurrentUser();

  const [name, setName] = useState("");
  const [poStartNumber, setPoStartNumber] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [timezone, setTimezone] = useState(null);
  const [timecardCycle, setTimecardCycle] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);

  const fileInputRef = useRef(null);

  const [yesButtonTitle, setYesButtonTitle] = useState("ADD");

  useEffect(() => {
    if (branch) {
      setName(branch.name);
      setPoStartNumber(branch.poStartNumber);
      setAddress(branch.address);
      setPhone(formatUSPhone(branch.phone));
      setTimezone(branch.timezone);
      setTimecardCycle(branch.timecardCycle ?? null);

      if (branch.logo) {
        setLogoPreview(getBranchLogoUrl(branch.id));
      }

      setYesButtonTitle("UPDATE");
    } else {
      setYesButtonTitle("ADD");
    }
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setRemoveLogo(false);
  };

  const addClicked = () => {
    if (branch) {
      branch.name = name;
      branch.poStartNumber = poStartNumber;
      branch.address = address;
      branch.phone = phone;
      branch.timezone = timezone;
      branch.timecardCycle = timecardCycle;

      onUpateClicked(branch, logoFile, removeLogo);
    } else {
      const data = {
        name,
        poStartNumber,
        address,
        phone,
        timezone,
        timecardCycle,
        company: currentUser.company,
      };
      onAddClicked(data, logoFile);
    }
  };

  const cancelClicked = () => {
    onCancelClicked();
  };

  return (
    <div
      onClick={onCancelClicked}
      className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-30"
    >
      <div onClick={(e) => e.stopPropagation()} className="w-[448px] bg-white rounded-[6px] p-[18px] flex flex-col">
        <p className="text-[16px] font-OpenSansMedium font-[600] text-titleColor mb-5">
          {branch ? "EDIT BRANCH" : "ADD BRANCH"}
        </p>

        <div className="flex flex-col justify-between h-full">
          <div className="w-full mb-3">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Logo</p>
            <div className="flex items-center gap-3">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-[80px] h-[80px] border-dashed border-[2px] border-gray-300 rounded-[6px] flex items-center justify-center cursor-pointer overflow-hidden hover:border-blueColor transition-colors"
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-gray-400 text-[24px]">+</span>
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] text-gray-500">Upload branch logo</span>
                <span className="text-[10px] text-gray-400">PNG, JPG (max 2MB)</span>
                {logoPreview && (
                  <span
                    onClick={() => {
                      setLogoPreview(null);
                      setLogoFile(null);
                      setRemoveLogo(true);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="text-[10px] text-red-500 cursor-pointer mt-1 hover:underline"
                  >
                    Remove
                  </span>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleLogoChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="h-[60px] w-full">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Name</p>
            <div className="w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px]">
              <input
                value={name}
                type="text"
                onChange={(e) => setName(e.target.value)}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder="Enter branch name"
              />
            </div>
          </div>

          <div className="h-[60px] w-full mt-3">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">PO Start Number</p>
            <div className="w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px]">
              <input
                value={poStartNumber}
                type="number"
                onChange={(e) => setPoStartNumber(e.target.value)}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder="Enter branch name"
              />
            </div>
          </div>

          <div className="h-[60px] w-full mt-3">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Address</p>
            <div className="w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px]">
              <input
                value={address}
                type="text"
                onChange={(e) => setAddress(e.target.value)}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder="Enter branch address"
              />
            </div>
          </div>

          <div className="h-[60px] w-full mt-3">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Phone</p>
            <div className="w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px]">
              <input
                value={phone}
                type="text"
                onChange={(e) => setPhone(formatUSPhone(e.target.value))}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder="Enter branch phone"
              />
            </div>
          </div>

          <div className="h-[60px] w-full mt-3">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Timecard Cycle</p>
            <TimecardCycleDropdown
              timecardCycle={timecardCycle}
              timecardCycleSelected={setTimecardCycle}
              isEditing={!!branch}
            />
          </div>

          <div className="h-[60px] w-full mt-3">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Timezone</p>
            <TimezoneDropdown timezone={timezone} timezoneSelected={setTimezone} />
          </div>

          <div className=" w-full mt-3">
            <div className="flex items-center justify-between gap-4">
              <div
                onClick={cancelClicked}
                className="w-[50%] h-[30px] text-[10px] rounded-[6px] border-[1px] border-subTitleColor flex items-center justify-center text-subTitleColor cursor-pointer"
              >
                CANCEL
              </div>
              <div
                onClick={addClicked}
                className="w-[50%] h-[30px] text-[10px] rounded-[6px] flex items-center justify-center bg-blueColor text-white cursor-pointer"
              >
                {yesButtonTitle}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddModal;
